// src/oura/components/onboarding/StepsList.tsx
//
// The onboarding milestones, high level: payment, the first email, the
// client's setup, the First AI check, the results and plan. Each row says
// where it stands in 1 word, carries 1 fact on the right, and opens the thing
// that moves it on. A row that cannot start yet says what it waits for, so no
// row is a grey line with nothing to read.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { CheckIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { FOCUS_RING, TYPE, ROW_HOVER } from "@oura/ui";
import type { Milestone, StepState } from "../../onboarding/derive";

/** The mark before each step. Colour and shape both carry the state. */
function Mark({ state }: { state: StepState }) {
    if (state === "done") {
        return (
            <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-white">
                <CheckIcon size={12} strokeWidth={3} />
            </span>
        );
    }
    const ring: Record<Exclude<StepState, "done">, string> = {
        ours: "border-blue-600",
        oura: "border-violet-500",
        theirs: "border-gray-400",
        later: "border-gray-300",
    };
    const dot: Record<Exclude<StepState, "done">, string> = {
        ours: "bg-blue-600",
        oura: "bg-violet-500 motion-safe:animate-pulse",
        theirs: "bg-gray-400",
        later: "bg-transparent",
    };
    return (
        <span className={cn("grid h-5 w-5 place-items-center rounded-full border-2 bg-white", ring[state])}>
            <span className={cn("h-2 w-2 rounded-full", dot[state])} />
        </span>
    );
}

/** What each state is called, for a screen reader: the mark alone says it by colour. */
const STATE_WORD: Record<StepState, string> = {
    done: "Done",
    ours: "Needs us",
    oura: "Oura is working",
    theirs: "Waiting on client",
    later: "Not yet",
};

export interface StepAction {
    onClick?: () => void;
    to?: string;
}

function Row({ step, action }: { step: Milestone; action: StepAction }) {
    const later = step.state === "later";
    // A step that cannot start yet has nothing to open.
    const interactive = !later && Boolean(action.onClick || action.to);
    const body: ReactNode = (
        <>
            <span aria-hidden className="flex w-5 shrink-0 justify-center"><Mark state={step.state} /></span>
            {/* The name never gives way: on a phone the fact moves under it. */}
            <span className="flex min-w-0 flex-1 flex-col py-1.5 sm:flex-row sm:items-center sm:gap-3 sm:py-0">
                <span className={cn(TYPE.tb, "min-w-0 sm:flex-1 sm:truncate", later ? "text-gray-400" : "text-gray-900", interactive && "group-hover:text-blue-600")}>
                    {step.title}
                    <span className="sr-only">, {STATE_WORD[step.state]}</span>
                </span>
                {step.fact && (
                    <span className={cn(TYPE.tc, "tabular-nums sm:shrink-0 sm:whitespace-nowrap", step.late ? (step.state === "theirs" ? "font-medium text-gray-700" : "font-medium text-blue-700") : later ? "text-gray-400" : "text-gray-500")}>
                        {step.fact}
                    </span>
                )}
            </span>
            <span aria-hidden className="w-3.5 shrink-0">
                {interactive && <ChevronRightIcon size={14} className="text-gray-300 group-hover:text-gray-500" />}
            </span>
        </>
    );
    const shell = cn(
        "group flex min-h-11 w-full items-center gap-3 rounded-md text-left",
        interactive && ROW_HOVER,
        FOCUS_RING,
    );
    if (interactive && action.to) return <Link to={action.to} className={shell} data-step={step.key}>{body}</Link>;
    if (interactive && action.onClick) return <button type="button" onClick={action.onClick} className={shell} data-step={step.key}>{body}</button>;
    return <div className={shell} data-step={step.key}>{body}</div>;
}

export function StepsList({ steps, actionFor }: { steps: Milestone[]; actionFor: (step: Milestone) => StepAction }) {
    return (
        <div className="divide-y divide-gray-100">
            {steps.map((step) => <Row key={step.key} step={step} action={actionFor(step)} />)}
        </div>
    );
}
